import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const GoogleIcon = () => (
  <svg viewBox="0 0 48 48" className="h-4 w-4" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.5l6.7-6.7C35.6 2.4 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.9 6.1C12.4 13.2 17.7 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.1 24.6c0-1.6-.1-2.8-.4-4.1H24v8.2h12.5c-.3 2.1-1.6 5.2-4.7 7.3l7.6 5.9c4.5-4.2 6.7-10.3 6.7-17.3z" />
    <path fill="#FBBC05" d="M10.5 28.7A14.4 14.4 0 0 1 9.7 24c0-1.6.3-3.2.8-4.7l-7.9-6.1A24 24 0 0 0 0 24c0 3.9.9 7.5 2.6 10.8l7.9-6.1z" />
    <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.6-5.9c-2 1.4-4.7 2.4-8.3 2.4-6.3 0-11.6-3.7-13.5-9l-7.9 6.1C6.5 42.6 14.6 48 24 48z" />
  </svg>
);

const demos = [
  { rol: "Tutor (2 hijos)", email: "tutor@demo.com", pass: "Tutor123!" },
  { rol: "Docente", email: "docente@demo.com", pass: "Docente123!" },
  { rol: "Coordinadora", email: "coordinadora@demo.com", pass: "Coord123!" },
];

const PORTADA = "https://images.unsplash.com/photo-1719559519182-698f9bfc4e2f";
const LOGO = "https://customer-assets-cm19k8pv.emergentagent.net/job_school-hub-1220/artifacts/eggcq1ix_302319671_479406710859175_2268986965834108785_n.jpg";

export default function Login() {
  const { login, config } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const portada = config.tiene_portada
    ? `${API}/configuracion/imagen/portada?v=${config.imagen_version}` : PORTADA;
  const logo = config.tiene_logo
    ? `${API}/configuracion/imagen/logo?v=${config.imagen_version}` : LOGO;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const res = await login(email, password);
    setBusy(false);
    if (!res.ok) return setError(res.error);
    navigate(res.user.rol === "tutor" ? "/portal" : res.user.rol === "docente" ? "/docente"
      : res.user.rol === "coordinadora" ? "/coordinacion" : "/admin");
  };

  const entrarConGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = `${window.location.origin}/auth/google`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden overflow-hidden bg-primary lg:block grain">
        <img
          src={portada}
          alt="Portada del colegio"
          data-testid="login-portada"
          className="absolute inset-0 h-full w-full object-cover opacity-40"
        />
        <div className="relative z-10 flex h-full flex-col justify-between p-12 text-primary-foreground">
          <div className="flex items-center gap-3">
            <img
              src={logo}
              alt={config.nombre}
              className="h-24 w-auto rounded-xl bg-white object-contain p-2 shadow-lg"
              data-testid="login-logo"
            />
            <p className="font-display text-lg font-bold">{config.nombre}</p>
          </div>
          <div className="max-w-md">
            <h1 className="font-display text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl">
              Todo el colegio de sus hijos, en un solo lugar.
            </h1>
            <p className="mt-6 text-base leading-relaxed text-primary-foreground/85">
              Calificaciones, tareas, actividades, estado de cuenta y comprobantes fiscales (NCF) con la claridad
              que su familia merece.
            </p>
          </div>
          <p className="text-xs uppercase tracking-[0.25em] text-primary-foreground/70">
            Año lectivo {config.anio_lectivo} · Santo Domingo, R.D.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md rise">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Acceso al sistema</p>
          <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight">Iniciar sesión</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Use el correo registrado en el centro educativo.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email">Correo o teléfono</Label>
              <Input id="email" data-testid="login-email" type="email" value={email} autoComplete="email"
                     onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Contraseña</Label>
              <Input id="password" data-testid="login-password" type="password" value={password}
                     autoComplete="current-password" onChange={(e) => setPassword(e.target.value)} required />
            </div>
            {error && (
              <p className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive" data-testid="login-error">
                {error}
              </p>
            )}
            <Button type="submit" className="w-full rounded-full py-6 text-base" disabled={busy}
                    data-testid="login-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Entrar al portal"}
            </Button>
          </form>

          <div className="mt-6 flex items-center gap-4">
            <span className="h-px flex-1 bg-border" />
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">o</span>
            <span className="h-px flex-1 bg-border" />
          </div>

          <Button type="button" variant="outline" onClick={entrarConGoogle}
                  className="mt-6 w-full gap-3 rounded-full py-6 text-base" data-testid="login-google">
            <GoogleIcon /> Entrar con Google
          </Button>
          <p className="mt-3 text-center text-xs text-muted-foreground">
            Use el mismo correo que tiene registrado en el colegio.
          </p>

          {config.demo_habilitado && (
            <div className="mt-10 rounded-lg border border-border bg-card p-6" data-testid="panel-demo">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
                <ShieldCheck className="h-4 w-4" /> Cuentas de demostración
              </div>
              <div className="mt-4 space-y-2">
                {demos.map((d) => (
                  <button key={d.email} type="button"
                          data-testid={`demo-${d.email}`}
                          onClick={() => { setEmail(d.email); setPassword(d.pass); }}
                          className="flex w-full items-center justify-between rounded-md px-3 py-2 text-left text-sm lift hover:bg-muted">
                    <span className="font-medium">{d.rol}</span>
                    <span className="font-mono text-xs text-muted-foreground">{d.email}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <p className="mt-6 text-center text-sm text-muted-foreground">
            ¿Es padre nuevo en el colegio?{" "}
            <Link to="/registro" className="font-medium text-primary hover:underline" data-testid="ir-a-registro">
              Solicite su acceso aquí
            </Link>
          </p>
          <p className="mt-2 text-center text-sm text-muted-foreground">
            ¿Es docente?{" "}
            <Link to="/registro-docente" className="font-medium text-primary hover:underline"
                  data-testid="ir-a-registro-docente">
              Regístrese aquí
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
